import { samplesConfigObj } from '@/components/Glossary/Sections/SampleTypes/SamplesConfig';
import { DateTimePickerNative } from '@/components/UI/DateTimePickerNative/DateTimePickerNative';
import InputWithUnits from '@/components/UI/FormElements/InputWithUnits';
import { metadataFieldBuilder } from '@/components/UI/FormFields/FormField.utils';
import Timepoint from '@/components/UI/FormFields/Latest/Timepoint';
import FormRender from '@/components/UI/FormRender';
import Select from '@/components/UI/Select';
import type { SelectOption } from '@/components/UI/Select/Select';
import { _isNotEmpty, _notNil } from '@/littledash';
import type { TeamGlossary } from '@/model/Glosary.model';
import type { MetadataItem } from '@/model/Metadata.model';
import type { BulkSamplesFormData } from '@/model/Sample.model';
import { featuresSelector } from '@/support/Selectors';
import { DateUtils } from '@/utils/Date.utils';
import { ModalActions } from '@/utils/modal';
import { ErrorMessage } from '@hookform/error-message';
import { createSelector } from '@reduxjs/toolkit';
import { useState, type FC } from 'react';
import { Controller, FormProvider, useController, useForm, useFormContext } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { toFiniteNumberOrNull } from './BulkCreateSamples.utils';

const getDetailsDefaults = () =>
  Object.entries(samplesConfigObj).reduce<Record<string, { unit: string; value: null | string }>>(
    (acc, [key, value]) => {
      acc[key] = { unit: value.units[0], value: null };
      return acc;
    },
    {}
  );

const samplesDefaultObj = getDetailsDefaults();

interface DetailFieldProps {
  detail: string;
}

const DetailField: FC<DetailFieldProps> = ({ detail }) => {
  const {
    formState: { errors, isSubmitting },
    control,
  } = useFormContext<BulkSamplesFormData>();

  const units = samplesConfigObj?.[detail]?.units ?? [];

  return (
    <div className="w-50 pa2" data-testid="bulk-sample-detail-input">
      <label htmlFor={`details.${detail}`}>{samplesConfigObj?.[detail]?.title}</label>
      <Controller
        name={`details.${detail}` as `details.${keyof NonNullable<BulkSamplesFormData['details']>}`}
        control={control}
        render={({ field: { name, value, onChange, onBlur } }) => {
          return (
            <InputWithUnits
              name={name}
              value={toFiniteNumberOrNull(value?.value)}
              unit={units.length > 1 ? value?.unit : units[0]}
              onChange={onChange}
              onBlur={onBlur}
              classes="w-100"
              disabled={isSubmitting}
              hasError={_notNil(errors?.details?.[detail as keyof NonNullable<BulkSamplesFormData['details']>])}
              {...(units.length > 1
                ? { unitOptions: units.map((unit) => ({ label: unit, value: unit })) }
                : { unit: units[0] })}
            />
          );
        }}
      />
    </div>
  );
};

interface BulkCreateSamplesFormProps {
  sampleMetadata?: Array<MetadataItem>;
  sampleTypeOptions?: Array<TeamGlossary>;
  handleCallback: (formData: BulkSamplesFormData, metadata?: Array<MetadataItem>) => void;
  closeModal: () => void;
}

const featureSelector = createSelector([featuresSelector], (features) => ({
  plannedSamplesEnabled: features?.planned_samples ?? false,
}));

const BulkCreateSamplesForm: FC<BulkCreateSamplesFormProps> = ({
  sampleMetadata,
  sampleTypeOptions,
  handleCallback,
  closeModal,
}) => {
  const [sampleDetails, setSampleDetails] = useState<Array<string>>([]);
  const { plannedSamplesEnabled } = useSelector(featureSelector);

  const formMethods = useForm<BulkSamplesFormData>({
    mode: 'onSubmit',
    reValidateMode: 'onChange',
    defaultValues: {
      sample_type: '' as unknown as TeamGlossary,
      details: samplesDefaultObj as unknown as BulkSamplesFormData['details'],
      date: DateUtils.dateTimeNow(),
      meta: {},
      comments: '',
      timepoint: '',
    },
  });

  const {
    handleSubmit,
    control,
    formState: { isSubmitting, errors },
    register,
    getValues,
    reset,
  } = formMethods;

  const {
    field: { onBlur: sampleTypeOnBlur, value: sampleTypeValue },
  } = useController({
    control,
    name: 'sample_type',
    rules: { required: true },
  });

  const handleFormSubmit = (formData: BulkSamplesFormData) => {
    closeModal();
    handleCallback(formData, sampleMetadata);
  };

  const handleSampleTypeChange = (
    newValue: Readonly<SelectOption<TeamGlossary> | Array<SelectOption<TeamGlossary>>> | null | undefined
  ) => {
    if (_isNotEmpty(newValue) && !Array.isArray(newValue)) {
      const selected = (newValue as SelectOption<TeamGlossary>).value;
      setSampleDetails(selected.options?.details ?? []);
      reset({
        ...getValues(),
        sample_type: selected,
        details: samplesDefaultObj as unknown as BulkSamplesFormData['details'],
      });
    }
  };

  const sampleTypeApiId = sampleTypeValue?.api_id;

  return (
    <FormProvider {...formMethods}>
      <form
        data-testid="bulk-samples-form"
        className="pv3 overflow-auto h-100"
        style={{ maxHeight: 600 }}
        onSubmit={handleSubmit(handleFormSubmit)}
      >
        <div className="ph3 pt3 w-100 flex g3">
          <div className="w-50" data-testid="bulk-samples-date-controller">
            <Controller
              name="date"
              control={control}
              render={({ field: { onChange, value } }) => (
                <>
                  <label htmlFor="date">Date taken</label>
                  <DateTimePickerNative value={value} onChange={onChange} />
                </>
              )}
            />
            <ErrorMessage
              errors={errors}
              name="date"
              render={({ message }) => (
                <p className="f6 red db pb3" style={{ marginTop: '-9px' }}>
                  {message}
                </p>
              )}
            />
          </div>

          <div className="w-50" data-testid="bulk-samples-type-select">
            <label htmlFor="sample_type">Sample type (Required)</label>
            <Select
              value={
                _notNil(sampleTypeApiId)
                  ? { label: sampleTypeValue?.title ?? '', value: sampleTypeValue as TeamGlossary }
                  : null
              }
              options={sampleTypeOptions?.map((sampleType) => ({
                label: sampleType.title ?? '',
                value: sampleType,
              }))}
              onChange={handleSampleTypeChange}
              onBlur={sampleTypeOnBlur}
              className="w-100"
              isMulti={false}
            />
          </div>
        </div>
        {_isNotEmpty(sampleDetails) && (
          <div className="w-100 pa2 flex justify-between flex-wrap">
            {sampleDetails.map((detail) => (
              <DetailField key={`${detail}-${sampleTypeApiId}`} detail={detail} />
            ))}
          </div>
        )}
        {plannedSamplesEnabled && (
          <div className="w-100 ph3 mb3">
            <Timepoint name="timepoint" label="Timepoint" allow_zero allow_negative />
          </div>
        )}
        <div className="mb3 ph3 w-100">
          <label htmlFor="comments">Comments</label>
          <textarea
            id="comments"
            data-testid="bulk-sample-comment"
            style={{ marginBottom: 0 }}
            {...register('comments', {
              maxLength: {
                value: 255,
                message: 'Must be a maximum of 255 characters',
              },
            })}
          />
        </div>
        <div className="w-100 ph3">
          <FormRender fields={sampleMetadata?.map((meta) => metadataFieldBuilder(meta))} />
        </div>
      </form>
      <ModalActions
        submitBtnText="Next"
        submitButtonProps={{
          className: 'ml3',
          disabled: isSubmitting || !_notNil(sampleTypeApiId) || (plannedSamplesEnabled && _notNil(errors.timepoint)),
          loading: isSubmitting,
          loadingText: 'Generating samples',
        }}
        onSubmit={handleSubmit(handleFormSubmit)}
        className="pa3 flex self-end bt b--moon-gray w-100"
      />
    </FormProvider>
  );
};

export default BulkCreateSamplesForm;
